import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePrefs } from '../contexts/PrefsContext';
import { usePlay } from '../contexts/PlayContext';
import { realFavoritesCount } from '../utils/pageBreakUtils';

/**
 * How far along the visitor is, as a number 0-5, **latched once per page
 * load**.
 *
 * The states, in order. Each is "hasn't yet done what the next one needs":
 *
 *   0  no record at all
 *   1  a record, but nothing favorited yet
 *   2  has a playlist, but has never logged a score
 *   3  has scored, but has no credentials
 *   4  signed in, but has never shared
 *   5  done - nothing left to say
 *
 * **States 1 and 5 render nothing.** State 1 is silent because the heart/✕
 * instruction it used to carry already lives in the Editor's Picks block,
 * attached to the actual buttons - saying it twice on one page was worse than
 * saying it once, next to the thing it describes.
 *
 * **It does not change for the rest of the page load** - not when you act, and
 * not when you navigate. Hearting a game on Browse moves you from 1 to 2 in
 * the data, but the message settles into its new state on your next visit
 * rather than rewriting itself under you. That's the behaviour, not an
 * oversight, and it's why the latch is a module-level value: see below.
 *
 * The latch waits for real data rather than firing on mount. There's no read
 * cache (D25), so at mount everyone looks like state 0 - an empty `favorites`
 * array is the loading state as well as a real answer. State 0 itself is the
 * exception: a visitor with no record has nothing to wait for.
 *
 * State 1 clears on **one favorite**. It briefly also required a dismissal;
 * that trapped anyone who only ever hearts games on a state they could never
 * leave, which also put the sign-up message out of their reach.
 */

export const LADDER_UNKNOWN = null;

// The latch lives at module scope, not in the hook.
//
// It has to: `useLadderState` is called from Home, so component state dies
// whenever Home unmounts - which is every trip to Browse, /stats or the score
// form. That made "decided once per page load" actually mean "decided once per
// visit to Home", and the two routes people leave Home *for* are exactly the
// ones where they favorite a game or log a score. Module scope resets only on
// a real page load, which is what was wanted.
//
// `latchedIdentity` is the one thing that can force a re-evaluation without a
// reload, and it's deliberately the *credentialed* identity rather than the
// user id. A guest record coming into existence (null -> id) must not reset
// the latch, or hearting your first game would replace the intro mid-visit.
// Signing in or out must, or someone who just signed in would go on being told
// to sign in.
const ANONYMOUS = 'anonymous';
let latchedState = LADDER_UNKNOWN;
let latchedIdentity = null;

/** Tests only - the app has no reason to. */
export const resetLadderLatch = () => {
  latchedState = LADDER_UNKNOWN;
  latchedIdentity = null;
};

export const computeLadderState = ({
  user,
  isAuthenticated,
  favorites,
  hasEverScored,
  hasShared,
}) => {
  if (!user) return 0;
  // A page break alone isn't a playlist - only real games count here.
  if (realFavoritesCount(favorites) === 0) return 1;
  if (!hasEverScored) return 2;
  if (!isAuthenticated) return 3;
  if (!hasShared) return 4;
  return 5;
};

export const useLadderState = () => {
  const { user, isAuthenticated } = useAuth();
  const { favorites, prefsLoading } = usePrefs();
  const { hasEverScored, hasShared, friendsData } = usePlay();

  const identity = isAuthenticated && user ? user.id : ANONYMOUS;

  // Seeded from the module latch so a remount renders the settled answer
  // immediately, rather than flashing nothing for one frame before the effect
  // restores it.
  const [latched, setLatched] = useState(
    () => (latchedIdentity === identity ? latchedState : LADDER_UNKNOWN)
  );

  useEffect(() => {
    // Already decided for this identity, on this page load - reuse it.
    if (latchedIdentity === identity) {
      setLatched(latchedState);
      return;
    }

    // Nothing to load for a visitor with no record.
    if (!user) {
      latchedIdentity = identity;
      latchedState = 0;
      setLatched(0);
      return;
    }

    // Both round trips have to land, or the answer is a guess.
    if (prefsLoading || !friendsData) return;

    latchedIdentity = identity;
    latchedState = computeLadderState({
      user,
      isAuthenticated,
      favorites,
      hasEverScored,
      hasShared,
    });
    setLatched(latchedState);
  }, [identity, user, isAuthenticated, favorites, hasEverScored, hasShared, prefsLoading, friendsData]);

  return latched;
};
